import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../shared/api.js';
import { useApiMutation } from '../../../shared/useApi.js';

const KEY = ['subscriptions'];

export function useSubscriptions(filters = {}) {
  return useQuery({
    queryKey: [...KEY, 'list', filters],
    queryFn: async () => {
      const { data } = await api.get('/subscriptions', { params: filters });
      return Array.isArray(data) ? data : (data?.items || []);
    },
    staleTime: 10_000
  });
}

/**
 * بحث الشاشة — حقل واحد للاسم ولتمرير البطاقة، بلا تبديل أوضاع.
 *
 * الاستجابة كائن (`results` لا `items`) كي تصل حقول تشخيص البطاقة والرسالة
 * مع النتائج: مُعترض api يفكّ مظروف `{items}` إلى مصفوفة فتضيع الحقول المجاورة.
 */
export function useSubscriptionSearch({ q, status, scanned } = {}) {
  const term = String(q || '').trim();
  return useQuery({
    queryKey: [...KEY, 'search', { q: term, status: status || null, scanned: !!scanned }],
    queryFn: async () => {
      const { data } = await api.get('/subscriptions/search', {
        params: { q: term || undefined, status: status || undefined, scanned: scanned ? true : undefined }
      });
      return data;
    },
    // نتائج فورية مع الكتابة: نُبقي السابقة ظاهرة فلا تَرتجف الشاشة
    placeholderData: (prev) => prev,
    staleTime: 5_000
  });
}

/**
 * إيصال عملية مسجَّلة — لإعادة الطباعة من عمود الإجراءات.
 * قراءة فقط: لا تُنشئ اشتراكاً ولا دفعة، ورقم الإيصال يبقى رقم الفاتورة الأصلي.
 */
export function fetchSubscriptionReceipt(id, periodId) {
  return api
    .get(`/subscriptions/${id}/receipt`, { params: periodId ? { periodId } : undefined })
    .then((r) => r.data);
}

export function useSubscriptionsReport({ from, to } = {}) {
  return useQuery({
    queryKey: [...KEY, 'report', { from: from || null, to: to || null }],
    queryFn: async () => {
      const { data } = await api.get('/subscriptions/report', { params: { from, to } });
      return data;
    },
    staleTime: 30_000
  });
}

/**
 * تغطية الاشتراك لطفل واحد — يستعملها الاستقبال قبل فتح الجلسة.
 * الطفل الجديد (بلا معرّف) لا يُستعلم عنه إطلاقاً: لا يمكن أن يكون مشتركاً.
 */
export function useChildCoverage(childId) {
  return useQuery({
    queryKey: [...KEY, 'coverage', childId || null],
    enabled: !!childId,
    queryFn: async () => {
      const { data } = await api.get(`/subscriptions/child/${childId}`);
      return data;
    },
    staleTime: 15_000
  });
}

function invalidate(qc) {
  qc.invalidateQueries({ queryKey: KEY });
  // الاشتراك عملية نقدية: الدرج والشفت والفواتير تغيّرت
  qc.invalidateQueries({ queryKey: ['shift'] });
  qc.invalidateQueries({ queryKey: ['cashboxes'] });
  // قد يكون أُنشئ طفل/ولي أمر/بطاقة، أو عُبِّئ رصيد بطاقة
  qc.invalidateQueries({ queryKey: ['children'] });
  qc.invalidateQueries({ queryKey: ['cards'] });
}

export function useSellSubscription() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (body) => api.post('/subscriptions', body).then((r) => r.data),
    successMessage: 'تم تسجيل الاشتراك الشهري',
    onSuccess: () => invalidate(qc)
  });
}

export function useRenewSubscription() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: ({ id, body }) => api.post(`/subscriptions/${id}/renew`, body || {}).then((r) => r.data),
    successMessage: 'تم تجديد الاشتراك',
    onSuccess: () => invalidate(qc)
  });
}

export function useCancelSubscription() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: ({ id, reason }) => api.post(`/subscriptions/${id}/cancel`, { reason }).then((r) => r.data),
    successMessage: 'تم إلغاء الاشتراك',
    onSuccess: () => invalidate(qc)
  });
}

export const STATUS_LABELS = {
  active: 'فعال',
  expired: 'منتهي',
  cancelled: 'ملغى'
};

export const STATUS_VARIANTS = {
  active: 'success',
  expired: 'warning',
  cancelled: 'danger'
};
